<template>
  <wrapper-page ref="wrapper">
    <div class="container-fluid py-3">
      <!-- Page Header -->
      <div class="row mb-3 align-items-center">
        <div class="col">
          <h3 class="mb-0 text-dark">Certificate Endorsement History</h3>
        </div>
      </div>

      <!-- Filter Controls Card -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="row g-3">
            <!-- ROW 1 -->
            <!-- Vessel Filter -->
            <div class="col-md-6 col-lg-3">
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <label
                  for="filter-vessel"
                  class="form-label font-weight-bold mb-0"
                  >Vessel</label
                >
                <div class="form-check form-check-inline me-0">
                  <input
                    id="enable-all-vessels"
                    class="form-check-input"
                    type="checkbox"
                    v-model="enableAll"
                    @change="getVesselsData"
                  />
                  <label
                    class="form-check-label small text-muted"
                    for="enable-all-vessels"
                  >
                    Show All
                  </label>
                </div>
              </div>
              <select
                id="filter-vessel"
                v-model="localFilters.vessel_id"
                class="form-control form-select form-control-sm"
                :disabled="loadingVessels"
              >
                <option :value="null">All Vessels</option>
                <option
                  v-for="vessel in uniqueVessels"
                  :key="vessel.vessel_id"
                  :value="vessel.vessel_id"
                >
                  {{ vessel.vessel_name }}
                </option>
              </select>
            </div>

            <!-- Certificate Type Filter -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-certificate"
                class="form-label font-weight-bold mb-1"
                >Certificate</label
              >
              <select
                id="filter-certificate"
                v-model="localFilters.certificate"
                class="form-control form-select form-control-sm"
              >
                <option :value="null">All Certificates</option>
                <option
                  v-for="cert in certificateOptions"
                  :key="cert.value"
                  :value="cert.value"
                >
                  {{ cert.text }} ({{ cert.value }})
                </option>
              </select>
            </div>

            <!-- Vessel Type Filter -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-vessel-type"
                class="form-label font-weight-bold mb-1"
                >Vessel Type</label
              >
              <select
                id="filter-vessel-type"
                v-model="localFilters.vessel_type"
                class="form-control form-select form-control-sm"
              >
                <option :value="null">All Types</option>
                <option
                  v-for="vt in vesselTypesOptions"
                  :key="vt.id"
                  :value="vt.id"
                >
                  {{ vt.type }}
                </option>
              </select>
            </div>

            <!-- Endorsed By Filter -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-endorsed-by"
                class="form-label font-weight-bold mb-1"
                >Endorsed By</label
              >
              <select
                id="filter-endorsed-by"
                v-model="localFilters.endorsed_by"
                class="form-control form-select form-control-sm"
              >
                <option :value="null">All Users</option>
                <option
                  v-for="user in userOptions"
                  :key="user.id"
                  :value="user.id"
                >
                  {{ user.name || user.username }}
                </option>
              </select>
            </div>

            <!-- ROW 2 -->
            <!-- From Date -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-from-date"
                class="form-label font-weight-bold mb-1"
                >From Date</label
              >
              <input
                id="filter-from-date"
                v-model="localFilters.from"
                type="date"
                class="form-control form-control-sm"
              />
            </div>

            <!-- To Date -->
            <div class="col-md-6 col-lg-3">
              <label
                for="filter-to-date"
                class="form-label font-weight-bold mb-1"
                >To Date</label
              >
              <input
                id="filter-to-date"
                v-model="localFilters.to"
                type="date"
                class="form-control form-control-sm"
              />
            </div>

            <!-- Action Buttons (Aligned on the right of Row 2) -->
            <div
              class="col-12 col-lg-6 d-flex justify-content-end align-items-end mt-3 mt-lg-0"
            >
              <button
                type="button"
                class="btn btn-sm btn-secondary me-2"
                style="margin-right: 8px"
                @click="resetFilters"
              >
                Reset
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="applyFilters"
              >
                <i class="ri-search-line align-middle me-1"></i>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Table Card -->
      <div class="card shadow-sm">
        <div class="card-body">
          <certification-endorsement-history :filters="appliedFilters" />
        </div>
      </div>
    </div>
  </wrapper-page>
</template>

<script>
import WrapperPage from "@/components/layout/WrapperPage.vue";
import CertificationEndorsementHistory from "@/components/vessels/tabs/surveysAndCerts/CertificationHistory.vue";
import { getUsersProjectsVessels } from "@/apis/usersProjectsVessels";
import { getVesselTypes } from "@/apis/vessels";
import { VesselCertificates } from "../../constants/vessels";
import { notifyError } from "@/helpers/notification";

export default {
  name: "EndorsementHistoryPage",
  components: {
    WrapperPage,
    CertificationEndorsementHistory,
  },
  data() {
    return {
      vesselTypesOptions: [],
      usersProjectsVesselsData: [],
      enableAll: false,
      loadingVessels: false,

      // Staging inputs for the filter form
      localFilters: {
        vessel_id: null,
        certificate: null,
        vessel_type: null,
        endorsed_by: null,
        from: null,
        to: null,
      },

      // Submitted filters passed to the table component
      appliedFilters: {},
    };
  },
  computed: {
    // Computes a unique and alphabetically sorted array of vessels
    uniqueVessels() {
      const seen = new Set();
      const uniqueData = (this.usersProjectsVesselsData || []).filter(
        (item) => {
          if (!item.vessel_id) return false;
          const key = `${item.vessel_id}-${item.vessel_name}`;
          return seen.has(key) ? false : seen.add(key);
        }
      );

      return uniqueData.sort((a, b) =>
        (a.vessel_name || "").localeCompare(b.vessel_name || "")
      );
    },

    userOptions() {
      const users = [...(this.$store.state.defaultStore.users || [])];
      return users.sort((a, b) =>
        (a.name || a.username || "").localeCompare(b.name || b.username || "")
      );
    },

    certificateOptions() {
      const certs = [...(VesselCertificates || [])];
      return certs.sort((a, b) =>
        (a.text || a.value || "").localeCompare(b.text || b.value || "")
      );
    },
  },
  async mounted() {
    await Promise.all([this.getVesselsData(), this.fetchVesselTypesOptions()]);
  },
  methods: {
    async getVesselsData(userId = null) {
      this.loadingVessels = true;
      try {
        const params = { enable_all: this.enableAll };
        if (userId !== null) params.user_id = userId;

        const { data } = await getUsersProjectsVessels(params);
        this.usersProjectsVesselsData = data || [];

        // Reset selected vessel_id if it's no longer present in the updated unique list
        if (
          this.localFilters.vessel_id &&
          !this.uniqueVessels.some(
            (v) => v.vessel_id === this.localFilters.vessel_id
          )
        ) {
          this.localFilters.vessel_id = null;
        }
      } catch (err) {
        notifyError(this, err?.response?.data);
      } finally {
        this.loadingVessels = false;
      }
    },

    async fetchVesselTypesOptions() {
      try {
        const response = await getVesselTypes();
        const rawTypes = response.data || [];

        this.vesselTypesOptions = rawTypes.sort((a, b) =>
          (a.type || "").localeCompare(b.type || "")
        );
      } catch (err) {
        console.error("Failed to load vessel types:", err);
      }
    },

    applyFilters() {
      // Check if at least one filter has a valid value
      const hasActiveFilter = Object.values(this.localFilters).some(
        (val) => val !== null && val !== undefined && val !== ""
      );

      if (!hasActiveFilter) {
        notifyError(this, "Please select at least one filter.");
        return;
      }

      // Clone filters to trigger backend API call in the history component
      this.appliedFilters = { ...this.localFilters };
    },
    resetFilters() {
      this.localFilters = {
        vessel_id: null,
        certificate: null,
        vessel_type: null,
        endorsed_by: null,
        from: null,
        to: null,
      };
      this.applyFilters();
    },
  },
};
</script>
